import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import heroImage from "@/assets/wooden-island.jpg";

const HeroSection = () => {
  const scrollTo = (id: string) =>
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      className="relative min-h-[680px] lg:min-h-[760px] pt-20 bg-secondary overflow-hidden"
      aria-label="Welcome to Stoltzfus Cabinet Shop"
    >
      {/* Oversized ghosted monogram — bookplate watermark */}
      <span
        aria-hidden="true"
        className="monogram-watermark -right-12 top-24 lg:top-16 select-none"
      >
        SCS
      </span>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-0 lg:gap-8 items-stretch">
        {/* Left card */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5 lg:row-start-1 relative z-10 lg:mt-16 bg-background p-8 sm:p-12 lg:p-14 lg:mr-[-4rem] shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)] flex flex-col justify-center"
        >
          {/* Roman numeral index */}
          <p
            className="text-accent italic text-base mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.05em" }}
          >
            I.
          </p>

          {/* Eyebrow with hairline rule */}
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-primary/60" aria-hidden="true" />
            <p className="text-muted-foreground text-[0.6875rem] tracking-[0.35em] uppercase">
              Est. 1968 · Talmage, PA
            </p>
          </div>

          <h1
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span className="italic font-normal text-accent">Amish Crafted</span>
            <br />
            <span className="font-medium">Cabinets</span>
          </h1>
          <p className="text-muted-foreground leading-relaxed mb-8 max-w-md">
            Three generations of custom cabinetry, hand-built in our Lancaster County
            shop with the care of a family that's been doing this since 1968.
          </p>
          <button
            onClick={() => scrollTo("#contact")}
            className="group inline-flex items-center gap-3 self-start bg-primary text-primary-foreground px-8 py-4 text-xs tracking-[0.25em] uppercase hover:bg-accent transition-colors duration-500"
          >
            Get In Touch
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>

          {/* Hairline + plate caption */}
          <div className="mt-12 flex items-center gap-4">
            <span className="h-px w-16 bg-foreground/30" aria-hidden="true" />
            <p className="text-[0.6875rem] tracking-[0.28em] uppercase text-muted-foreground">
              No. 1968
            </p>
            <span className="h-px w-16 bg-foreground/30" aria-hidden="true" />
          </div>
        </motion.div>

        {/* Right image */}
        <div className="lg:col-span-7 lg:row-start-1 relative h-[360px] sm:h-[460px] lg:h-[600px] lg:mt-10 overflow-hidden">
          <img
            src={heroImage}
            alt="Custom Amish-crafted kitchen island in solid wood"
            className="absolute inset-0 w-full h-full object-cover"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
          />
          {/* Image caption — engraver's mark */}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
